import {
	ColorValueByShade,
	createComponentColorShades,
	PartialThemeDefinition,
	resolver,
} from "@animech/ui";
import { IconCodes } from "src/icons";

const fontPath = "resources/fonts";

const PrimaryShade: ColorValueByShade = {
	100: "#f5f5f5",
	200: "#e0e0e0",
	300: "#b8b8b8",
	400: "#6a6a6a",
	500: "#101010",
	600: "#0d0d0d",
	700: "#0a0a0a",
	800: "#070707",
	900: "#040404",
};

// White notification with black text; only the icon glyph carries the accent.
const flatNotificationColors = (accent: "alert" | "success") =>
	resolver(
		["colors.components", "colors.palette", "colors.backgrounds"],
		({ colors }) => ({
			foreground: colors.palette.black,
			background: colors.palette.white,
			border: colors.backgrounds[0].border,
			header: {
				foreground: colors.palette.black,
			},
			icon: {
				foreground: colors.components[accent].shades[500].main,
				background: colors.palette.white,
			},
		}),
	);

export const themeOverrides: PartialThemeDefinition = {
	colors: {
		backgrounds: [
			{ background: "rgb(255, 255, 255)", border: "rgb(230, 230, 230)" },
			{ background: "rgb(245, 245, 245)", border: "rgb(220, 220, 220)" },
			{ background: "rgb(235, 235, 235)", border: "rgb(210, 210, 210)" },
			{ background: "rgb(220, 220, 220)", border: "rgb(190, 190, 190)" },
			{ background: "rgb(200, 200, 200)", border: "rgb(170, 170, 170)" },
			{ background: "rgb(170, 170, 170)", border: "rgb(140, 140, 140)" },
		],
		foregrounds: [
			"rgb(0, 0, 0)",
			"rgb(30, 30, 30)",
			"rgb(60, 60, 60)",
			"rgb(90, 90, 90)",
			"rgb(120, 120, 120)",
			"rgb(150, 150, 150)",
		],
		components: {
			primary: {
				shades: resolver(["colors.palette"], ({ colors }) =>
					createComponentColorShades(
						PrimaryShade,
						colors.palette.white,
						colors.palette.black,
						500,
					),
				),
			},
		},
	},
	components: {
		container: {
			sizes: {
				default: {
					paddingHorizontal: "0px",
					paddingVertical: "0px",
				},
			},
		},
		inputContainer: {
			marginBottom: "12px",
			mobileMarginBottom: "8px",
		},
		icon: {
			typography: {
				fontSize: "20px",
			},
			typographyCustom: {
				fontSize: "20px",
			},
		},
		button: {
			colors: {
				neutral: {
					solid: {
						default: {
							background: "rgb(255, 255, 255)",
						},
					},
				},
			},
			sizes: {
				small: {
					typographyGhost: {
						fontWeight: 300,
					},
					typographySolid: {
						fontWeight: 300,
					},
				},
				default: {
					paddingHorizontal: "16px",
					paddingVertical: "9px",
					typographyGhost: {
						fontSize: "16px",
						fontWeight: 300,
					},
					typographySolid: {
						fontSize: "16px",
						fontWeight: 300,
					},
				},
			},
		},
		underlineTabList: {
			typography: {
				fontWeight: 300,
			},
			activeTypography: {
				fontWeight: 300,
			},
			hoverTypography: {
				fontWeight: 300,
			},
			colors: {
				primary: {
					activeForeground: "rgb(0, 0, 0)",
					border: "rgb(0, 0, 0)",
					foreground: "rgb(100, 100, 100)",
					indicatorBackground: "rgb(0, 0, 0)",
					background: "transparent",
					hoverForeground: "rgb(30, 30, 30)",
				},
			},
			/*
				This value doesnt seem to work?
				Workaround found in: configuration-panel.style.tsx
			*/
			itemLabelSpacing: "32px",
			itemPaddingHorizontal: "12px",
			itemPaddingVertical: "6px",
			paddingVertical: "8px",
			indicatorHeight: "1px",
			borderWidth: "0px",
		},
		compactCardSelect: {
			labelTypography: {
				fontWeight: 300,
				lineHeight: "20px",
			},
			/*
				Keep on a power of two. Product thumbnails are decoded at mip sizes
				(1024/512/256/128), so a size that falls between two mips can force
				an upscale that visibly pixelates detailed images.
			*/
			imageSize: "128px",
			supportLabelSpacing: "4px",
			paddingHorizontal: "8px",
			paddingVertical: "8px",
			colors: {
				neutral: {
					default: {
						background: "transparent",
						border: "transparent",
					},
					hover: {
						background: "transparent",
						border: "transparent",
					},
					checked: {
						background: "transparent",
						border: "transparent",
					},
				},
			},
			mobile: {
				imageSize: "48px",
			},
		},
		textBox: {
			stepperWidth: "32px",
			typography: {
				fontWeight: 300,
				fontSize: "14px",
			},
			paddingVertical: "12px",
			paddingHorizontal: "8px",
		},
		notification: {
			colors: {
				alert: flatNotificationColors("alert"),
				success: flatNotificationColors("success"),
			},
		},
		notificationRoot: resolver("app.viewport", (viewport) => ({
			padding: viewport.padding,
			mobilePadding: viewport.mobilePadding,
		})),
		tooltip: {
			typography: {
				fontSize: "12px",
			},
		},
	},
	app: {
		modals: {
			maxWidth: "700px",
		},
		viewport: {
			padding: "10px",
		},
	},
	dimensions: {
		borderRadius: "4px",
	},
	icons: IconCodes,
	typography: {
		fontSize: {
			xSmall: "12px",
			small: "14px",
			base: "16px",
			large: "18px",
			xLarge: "20px",
			xxLarge: "22px",
			xxxLarge: "26px",
			xxxxLarge: "30px",
			xxxxxLarge: "36px",
		},
		fontWeight: {
			xLight: 100,
			light: 300,
			normal: 400,
			bold: 500,
			xBold: 700,
			xxBold: 900,
		},
		fontFace: {
			customIcons: {
				name: "string-icons",
				version: "1.0.1",
				variants: [
					{
						weight: "normal",
						style: "normal",
						sources: [`${fontPath}/icons/fonts/string-icons.woff`],
					},
				],
			},
		},
		fontFamily: {
			primary: `nimbus-sans, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
			secondary: `nimbus-sans, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
		},
	},
};
